import { ColumnType } from '@ecosystem/export-api';
import { ConfigItemValue } from '@ecosystem/newron-client-lib';
import { navigation } from '@ecosystem/newron-design-system';
import React, { useCallback, useEffect, useState } from 'react';

import { ExportPngInfo, ExportTableInfo, InsightErrorType, InsightRendererProps } from '../../../shared/types';

const { ProgressTracker } = navigation;

const base64Image = (base64Image: string) => `data:image/png;base64,${base64Image}`;

export interface PngData {
  dataUrl: string;
  width?: number;
  height?: number;
}
export interface PngRendererData {
  images: PngData[];
  focusedImageIndex?: number;
  configValues?: ConfigItemValue[];
  chartName?: string;
  throwError?: boolean;
  skipOnLoaded?: boolean;
  onExportReadyThrows?: boolean;
  errorType?: InsightErrorType;
  skipOnExportReady?: boolean;
  hasTimeout?: boolean;
  hasPropositionError?: boolean;
  hasConfigItemsError?: boolean;
  hasConfigValueError?: boolean;
  shouldFailInsightInfo?: boolean;
  resetErrorType?: boolean;
  allowDataTypeSwitch?: boolean;
  isTableExport?: boolean;
  helperText?: string;
  overrides?: Omit<PngRendererData, 'overrides'>;
}

export const PngRenderer: React.FC<InsightRendererProps<PngRendererData>> = ({
  insight,
  onLoaded,
  onExportReady,
  onError,
  onDataChanged,
}: InsightRendererProps<PngRendererData>) => {
  const { data: insightData } = insight;
  const [focusedImageIndex, setFocusedImageIndex] = useState(insightData.focusedImageIndex ?? 0);

  React.useEffect(() => {
    if (insightData.errorType) {
      onError({
        errorType: insightData.errorType,
        loggerMessage: 'Error flagged by proposition (enforced)',
      });
    }

    if (insightData.throwError) {
      throw new Error('Renderer Error (enforced)');
    }

    if (!insightData.skipOnLoaded) {
      if (insightData.allowDataTypeSwitch) {
        const delay = 2000; // Adding a 2 second delay to simulate loading
        const timer = setTimeout(() => {
          onLoaded();
        }, delay);
        return () => clearTimeout(timer);
      } else {
        onLoaded();
      }
    }

    return () => {
      if (insightData.resetErrorType) {
        const { errorType, resetErrorType, ...rest } = insightData;
        onDataChanged(rest);
      }
    };
  }, [onLoaded, onError, insightData, onDataChanged]);

  useEffect(() => {
    const exportPng = (): Promise<ExportPngInfo> =>
      insightData.onExportReadyThrows
        ? Promise.reject(new Error('On Export Ready Error (enforced)'))
        : Promise.resolve({
            images: (focusedImageIndex !== undefined
              ? [insightData.images[focusedImageIndex]]
              : insightData.images
            ).map(image => ({
              dataUrl: base64Image(image.dataUrl),
              width: image.width || 100,
              height: image.height || 100,
            })),
          });
    const exportTable = (): Promise<ExportTableInfo> =>
      insightData.onExportReadyThrows
        ? Promise.reject(new Error('On Export Ready Error (enforced)'))
        : Promise.resolve({
            table: {
              currencySymbol: '$',
              dataColumns: [
                { type: ColumnType.Text, header: 'First Name' },
                { type: ColumnType.Text, header: 'Last Name' },
              ],
              dataRows: [[{ value: 'Sample' }, { value: 'User' }]],
            },
          });
    if (!insightData.skipOnExportReady) {
      onExportReady({
        ...(insightData.isTableExport ? { exportTable } : { exportPng }),
        subtitle: insightData.chartName,
      });
    }
  }, [
    onExportReady,
    insightData.skipOnExportReady,
    insightData.onExportReadyThrows,
    insightData.isTableExport,
    insightData.images,
    insightData.chartName,
    focusedImageIndex,
  ]);

  const setSelectedDot = useCallback(
    (selectedDot: number) => {
      if (selectedDot !== focusedImageIndex) {
        setFocusedImageIndex(selectedDot);
        onDataChanged({ ...insightData, focusedImageIndex: selectedDot });
      }
    },
    [onDataChanged, focusedImageIndex, insightData]
  );

  return (
    <div id="png-renderer">
      <img
        src={base64Image(insightData.images.length > 0 ? insightData.images[focusedImageIndex].dataUrl : null)}
        alt="png-renderer"
        style={{ maxWidth: '100%' }}
        className="h-auto"
      />
      {insightData.images.length > 1 && (
        <div className="text-center my-10">
          <ProgressTracker
            steps={insightData.images.map((_, index) => index)}
            setSelectedDot={setSelectedDot}
            selectedDot={focusedImageIndex}
          />
        </div>
      )}
    </div>
  );
};
